import { Trans } from "react-i18next";
import { isEmpty } from "lodash";

import { SalsaWorkerPaymentReadinessEnum } from "types/BETypes";
import {
  EComprehensibleUserStatuses,
  getComprehensibleStatus,
} from "helpers/employee/userStatusMap";

import { IContextMenuOption } from "uikit";

import { UserLiteSearchResponseDto } from "utils/swagger_react_query";

import { WorkerContextMenuActionType } from "./types";

export type IMenuAction = (data: Partial<UserLiteSearchResponseDto>, callback?: () => void) => void;

export interface IEmployeesListContextMenuOptionsActions {
  onFinishSetup: IMenuAction;
  onInviteWorker: IMenuAction;
  onResendInvite: IMenuAction;
  onEditWorker: IMenuAction;
  onDeleteWorker: IMenuAction;
}

const useContextMenu = ({
  onFinishSetup,
  onInviteWorker,
  onResendInvite,
  onEditWorker,
  onDeleteWorker,
}: IEmployeesListContextMenuOptionsActions) => {
  const translationPrefix = `people_page.index.table.menu_actions`;

  const getMenuActions = (data: Partial<UserLiteSearchResponseDto>) => {
    return {
      FINISH_SETUP: {
        label: <Trans i18nKey={`${translationPrefix}.finish_setup`} />,
        value: WorkerContextMenuActionType.FINISH_SETUP,
        onClick: () => onFinishSetup(data),
      },
      INVITE_WORKER: {
        label: <Trans i18nKey={`${translationPrefix}.invite_worker`} />,
        value: WorkerContextMenuActionType.INVITE_WORKER,
        onClick: () => onInviteWorker(data),
      },
      RESEND_INVITE: {
        label: <Trans i18nKey={`${translationPrefix}.resend_invite`} />,
        value: WorkerContextMenuActionType.RESEND_INVITE,
        onClick: () => onResendInvite(data),
      },
      ON_EDIT_WORKER: {
        label: <Trans i18nKey={`${translationPrefix}.edit_worker`} />,
        value: WorkerContextMenuActionType.ON_EDIT_WORKER,
        onClick: () => onEditWorker(data),
      },
      ON_DELETE_WORKER: {
        label: <Trans i18nKey={`${translationPrefix}.delete_worker`} />,
        value: WorkerContextMenuActionType.ON_DELETE_WORKER,
        onClick: () => onDeleteWorker(data),
      },
    };
  };

  const getContextMenuOptions = (
    data: Partial<UserLiteSearchResponseDto>,
  ): Array<IContextMenuOption<WorkerContextMenuActionType> | undefined> => {
    const status = getComprehensibleStatus(data);

    const menuActions = getMenuActions(data);

    const statusActions = {
      [EComprehensibleUserStatuses.EMPLOYED]: [],
      [EComprehensibleUserStatuses.INVITED]: [menuActions.RESEND_INVITE],
      [EComprehensibleUserStatuses.PROSPECT]: [
        menuActions.INVITE_WORKER,
        menuActions.ON_EDIT_WORKER,
        menuActions.ON_DELETE_WORKER,
      ],

      //TODO::: Remove these statuses later, if they are deemed unnecessary
      [EComprehensibleUserStatuses.PENDING_EXIT]: [],
      [EComprehensibleUserStatuses.EXITED]: [],
      [EComprehensibleUserStatuses.IN_DEFAULT]: [],
      [EComprehensibleUserStatuses.NOT_READY]: [],
    };

    let result = status ? statusActions[status] : [];

    if (status === EComprehensibleUserStatuses.EMPLOYED) {
      if (data.paymentReadiness === SalsaWorkerPaymentReadinessEnum.READY) {
        result = [menuActions.ON_EDIT_WORKER];
      }
    }

    return result;
  };

  const getWorkerProfileActions = (
    data: Partial<UserLiteSearchResponseDto> | undefined,
  ): Array<IContextMenuOption<WorkerContextMenuActionType> | undefined> => {
    if (isEmpty(data)) return [];
    const status = getComprehensibleStatus(data);
    const menuActions = getMenuActions(data);

    const statusActions = {
      [EComprehensibleUserStatuses.EMPLOYED]: [menuActions.FINISH_SETUP],
      [EComprehensibleUserStatuses.INVITED]: [menuActions.RESEND_INVITE, menuActions.FINISH_SETUP],
      [EComprehensibleUserStatuses.PROSPECT]: [
        menuActions.INVITE_WORKER,
        menuActions.ON_EDIT_WORKER,
        menuActions.ON_DELETE_WORKER,
      ],

      //TODO::: Remove these statuses later, if they are deemed unnecessary
      [EComprehensibleUserStatuses.PENDING_EXIT]: [],
      [EComprehensibleUserStatuses.EXITED]: [],
      [EComprehensibleUserStatuses.IN_DEFAULT]: [],
      [EComprehensibleUserStatuses.NOT_READY]: [],
    };

    let result = status ? statusActions[status] : [];

    if (status === EComprehensibleUserStatuses.EMPLOYED) {
      if (data?.paymentReadiness === SalsaWorkerPaymentReadinessEnum.READY) {
        result = [menuActions.ON_EDIT_WORKER];
      }
    }

    return result;
  };

  return {
    getContextMenuOptions,
    getWorkerProfileActions,
  };
};

export default useContextMenu;
